// École Nid Douillet - Main React Application
// Root component with routing and authentication

import { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { useAuth } from '@store/authStore';
import { checkApiHealth } from '@services/api';

// Layout Components
import MainLayout from '@components/layout/MainLayout';
import AuthLayout from '@components/layout/AuthLayout';
import PublicLayout from '@components/layout/PublicLayout';

// Page Components
import LoginPage from '@pages/auth/LoginPage';
import DashboardPage from '@pages/dashboard/DashboardPage';
import ChildrenPage from '@pages/children/ChildrenPage';
import ParentsPage from '@pages/parents/ParentsPage';
import PaymentsPage from '@pages/payments/PaymentsPage';
import AnalyticsPage from '@pages/analytics/AnalyticsPage';
import SettingsPage from '@pages/settings/SettingsPage';
import ProfilePage from '@pages/profile/ProfilePage';
import CMSPage from '@pages/cms/CMSPage';

// Public Pages
import HomePage from '@pages/public/HomePage';
import AboutPage from '@pages/public/AboutPage';
import ContactPage from '@pages/public/ContactPage';
import NewsPage from '@pages/public/NewsPage';
import EnrollmentPage from '@pages/public/EnrollmentPage';

// Error Pages
import NotFoundPage from '@pages/error/NotFoundPage';
import UnauthorizedPage from '@pages/error/UnauthorizedPage';
import ServerErrorPage from '@pages/error/ServerErrorPage';

// Components
import LoadingSpinner from '@components/ui/LoadingSpinner';
import ProtectedRoute from '@components/auth/ProtectedRoute';
import ApiHealthChecker from '@components/system/ApiHealthChecker';

function App() {
  const { isAuthenticated, isLoading } = useAuth();

  // Check API health on mount
  useEffect(() => {
    const checkHealth = async () => {
      const isHealthy = await checkApiHealth();
      if (!isHealthy) {
        console.warn('API health check failed');
      }
    };
    
    checkHealth();
  }, []);

  // Show loading spinner during authentication check
  if (isLoading) {
    return (
      <div className="flex h-screen items-center justify-center bg-neutral-50">
        <div className="text-center">
          <LoadingSpinner size="lg" />
          <p className="mt-4 text-sm text-neutral-600">
            Chargement de l'École Nid Douillet...
          </p>
        </div>
      </div>
    );
  }

  return (
    <Router>
      <div className="App">
        {/* Toast notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#fff',
              color: '#374151',
              boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
            },
            success: {
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />

        {/* API Health Checker */}
        <ApiHealthChecker />

        {/* Main Routes */}
        <Routes>
          {/* Public Routes - Accessible at root level */}
          <Route path="/" element={
            <PublicLayout>
              <HomePage />
            </PublicLayout>
          } />
          <Route path="/a-propos" element={
            <PublicLayout>
              <AboutPage />
            </PublicLayout>
          } />
          <Route path="/contact" element={
            <PublicLayout>
              <ContactPage />
            </PublicLayout>
          } />
          <Route path="/news" element={
            <PublicLayout>
              <NewsPage />
            </PublicLayout>
          } />
          <Route path="/inscriptions" element={
            <PublicLayout>
              <EnrollmentPage />
            </PublicLayout>
          } />

          {/* Authentication Routes */}
          <Route path="/auth/*" element={
            isAuthenticated ? (
              <Navigate to="/dashboard" replace />
            ) : (
              <AuthLayout>
                <Routes>
                  <Route path="login" element={<LoginPage />} />
                  <Route path="*" element={<Navigate to="/auth/login" replace />} />
                </Routes>
              </AuthLayout>
            )
          } />

          {/* Protected Application Routes */}
          <Route path="/*" element={
            <ProtectedRoute>
              <MainLayout>
                <Routes>
                  {/* Dashboard */}
                  <Route path="dashboard" element={<DashboardPage />} />

                  {/* Children Management */}
                  <Route path="children/*" element={
                    <ProtectedRoute roles={['DIRECTOR']}>
                      <Routes>
                        <Route index element={<ChildrenPage />} />
                        <Route path=":id" element={<ChildrenPage />} />
                      </Routes>
                    </ProtectedRoute>
                  } />

                  {/* Parents Management */}
                  <Route path="parents/*" element={
                    <ProtectedRoute roles={['DIRECTOR']}>
                      <Routes>
                        <Route index element={<ParentsPage />} />
                        <Route path=":id" element={<ParentsPage />} />
                      </Routes>
                    </ProtectedRoute>
                  } />

                  {/* Payments */}
                  <Route path="payments/*" element={
                    <Routes>
                      <Route index element={<PaymentsPage />} />
                      <Route path=":id" element={<PaymentsPage />} />
                    </Routes>
                  } />

                  {/* Analytics */}
                  <Route path="analytics" element={
                    <ProtectedRoute roles={['DIRECTOR']}>
                      <AnalyticsPage />
                    </ProtectedRoute>
                  } />

                  {/* CMS */}
                  <Route path="cms" element={
                    <ProtectedRoute roles={['DIRECTOR']}>
                      <CMSPage />
                    </ProtectedRoute>
                  } />

                  {/* Settings */}
                  <Route path="settings" element={
                    <ProtectedRoute roles={['DIRECTOR']}>
                      <SettingsPage />
                    </ProtectedRoute>
                  } />

                  {/* Profile */}
                  <Route path="profile" element={<ProfilePage />} />

                  {/* Error Routes */}
                  <Route path="unauthorized" element={<UnauthorizedPage />} />
                  <Route path="server-error" element={<ServerErrorPage />} />

                  {/* Admin Dashboard Route */}
                  <Route path="/admin" element={
                    isAuthenticated ? (
                      <Navigate to="/dashboard" replace />
                    ) : (
                      <Navigate to="/auth/login" replace />
                    )
                  } />

                  {/* 404 - Not Found */}
                  <Route path="*" element={<NotFoundPage />} />
                </Routes>
              </MainLayout>
            </ProtectedRoute>
          } />
        </Routes>
      </div>
    </Router>
  );
}

export default App;
